import React, { useEffect, useState, useMemo } from "react";
import { intraStart, intraStop, intraStatus, intraCloseSignal, intraLessons, intraTradesPdfUrl, intraLearning } from "@/lib/api";
import { useAutoRoom } from "@/lib/autoWs";
import { toast } from "sonner";
import { Power, Zap, Loader2, X, Clock, Activity, ArrowUpRight, ArrowDownRight, ShieldX, CheckCircle2, Target, AlertTriangle, RefreshCw, Download, Brain } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import LiveClock from "@/components/LiveClock";
import LearningPanel from "@/components/LearningPanel";

const LEVERAGE_OPTIONS = [1, 2, 3, 5, 10, 15, 20];

export default function Part4Intraday() {
  const [config, setConfig] = useState({ capital: "100000", riskPercent: "1", leverage: 5 });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [lessons, setLessons] = useState({ lessons: [], count: 0 });
  const { connected, events, tape } = useAutoRoom();

  const intraMarks = useMemo(() => Object.fromEntries((tape?.positions?.intra || []).map(m => [m.id, m])), [tape]);
  const totalUnrealized = useMemo(() => (tape?.positions?.intra || []).reduce((a, m) => a + (Number(m.unrealized_pnl) || 0), 0), [tape]);

  const reload = async () => {
    try {
      const [s, l] = await Promise.all([intraStatus(), intraLessons().catch(() => ({ lessons: [], count: 0 }))]);
      setStatus(s);
      setLessons(l);
    } catch { /* */ }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); const iv = setInterval(reload, 30000); return () => clearInterval(iv); }, []);
  useEffect(() => { if (events.length) reload(); }, [events.length]);

  const bot = status?.bot;
  const active = bot?.active;
  const marketOpen = !!status?.market_open;
  const istTime = status?.ist_time;
  const activeSignals = status?.active_signals || [];
  const closedSignals = (status?.recent_signals || []).filter(s => s.status === "CLOSED").slice(0, 10);
  const totalRealized = closedSignals.reduce((acc, s) => acc + (Number(s.realized_pnl) || 0), 0);
  const portfolio = status?.latest_status;
  const watch = bot?.watchlist || [];

  const start = async () => {
    const cap = parseFloat(config.capital);
    const r = parseFloat(config.riskPercent);
    const lev = Number(config.leverage);
    if (!cap || cap < 1000) return toast.error("Capital must be >= ₹1,000");
    if (!r || r <= 0 || r > 25) return toast.error("Risk must be (0, 25]");
    if (!lev || lev < 1 || lev > 20) return toast.error("Leverage must be 1x-20x");
    setWorking(true);
    try {
      await intraStart({ capital: cap, risk_percent: r, leverage: lev, currency: "INR" });
      toast.success("NAVA-INTRA online");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Start failed"); }
    finally { setWorking(false); }
  };
  const stop = async () => {
    setWorking(true);
    try { await intraStop(); toast.success("Bot stopped"); reload(); }
    catch { toast.error("Stop failed"); }
    finally { setWorking(false); }
  };

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("tex_session_token");
      const r = await fetch(intraTradesPdfUrl(), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nava-intra-trades-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed");
    }
  };
  const closeSignal = async (id) => {
    try { await intraCloseSignal(id); toast.success("Signal closed"); reload(); }
    catch { toast.error("Failed"); }
  };

  if (loading) return <div className="text-center py-12 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div data-testid="part4-dashboard" className="space-y-6 overflow-x-hidden">
      {/* Hero */}
      <div className="relative border border-zinc-900 bg-gradient-to-br from-[#1A0F00] via-[#0A0A0A] to-[#0A0A0A] rounded-md overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
        <div className="relative px-5 md:px-8 py-6 md:py-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#FFCC00] mb-2">
              <Zap className="w-4 h-4" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em]">Part 4 · Indian Intraday Engine</span>
            </div>
            <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tight">NAVA-INTRA</h1>
            <p className="text-zinc-400 text-sm mt-2 max-w-xl">Same-day NSE cash-stock intraday scanner. AI screens setups, decides entries, and auto-closes by 3:15 PM IST. (Index options now live in Part 5.)</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <Pill label={connected ? "Stream Live" : "Reconnecting"} color={connected ? "#00E676" : "#FF3B30"} />
            <Pill label={marketOpen ? "NSE Open" : "NSE Closed"} color={marketOpen ? "#00E676" : "#A1A1AA"} icon={<Clock className="w-3 h-3" />} />
            <Pill label={active ? "Bot Active" : "Bot Stopped"} color={active ? "#FFCC00" : "#A1A1AA"} />
          </div>
        </div>
        {/* India market clock */}
        <div className="relative px-5 md:px-8 pb-5 flex flex-wrap items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">NSE Clock</span>
          <LiveClock zones={["IN"]} compact />
        </div>
      </div>

      {/* Disclaimer */}
      <div className="border border-[#FFCC00]/30 bg-[#FFCC00]/5 rounded-md p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-[#FFCC00] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[#FFCC00] leading-relaxed">
            <strong>Intraday signals — not financial advice.</strong> Live NSE prices via MoneyControl for cash equities only. NIFTY 50 index options are now in Part 2. Leverage amplifies both gains AND losses; with pure-notional sizing your loss can exceed risk-percent if SL slips. AI auto-squares off all positions at 3:15 PM IST.
          </p>
        </div>
      </div>

      {/* Control panel */}
      {!active ? (
        <div className="border border-zinc-900 bg-[#0F0F0F] rounded-md p-5 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <Field label="Capital (₹)" sub="Cash in your trading account">
              <input data-testid="intra-capital" type="number" value={config.capital} onChange={e => setConfig({ ...config, capital: e.target.value })}
                className="w-full bg-black border border-zinc-800 text-white px-3 py-2.5 rounded-sm font-mono focus:border-[#FFCC00] outline-none" />
            </Field>
            <Field label="Risk per Trade (%)" sub="Reference only (notional-mode sizing)">
              <input data-testid="intra-risk" type="number" step="0.5" value={config.riskPercent} onChange={e => setConfig({ ...config, riskPercent: e.target.value })}
                className="w-full bg-black border border-zinc-800 text-white px-3 py-2.5 rounded-sm font-mono focus:border-[#FFCC00] outline-none" />
            </Field>
            <Field label="Leverage" sub="Broker MIS margin multiplier">
              <div className="flex flex-wrap gap-1.5" data-testid="intra-leverage">
                {LEVERAGE_OPTIONS.map(l => (
                  <button key={l} type="button"
                    data-testid={`intra-leverage-${l}x`}
                    onClick={() => setConfig({ ...config, leverage: l })}
                    className={`px-3 py-2 rounded-sm border text-sm font-mono ${Number(config.leverage) === l ? "bg-[#FFCC00]/15 border-[#FFCC00] text-[#FFCC00]" : "bg-black border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                    {l}x
                  </button>
                ))}
              </div>
            </Field>
          </div>
          <div className="mb-5 text-xs text-zinc-400 font-mono bg-black/40 border border-zinc-900 rounded-sm px-3 py-2.5">
            Est. notional deployed: <span className="text-[#FFCC00] font-bold">₹{Number(config.capital || 0).toLocaleString("en-IN")} × {config.leverage}x = ₹{(Number(config.capital || 0) * Number(config.leverage)).toLocaleString("en-IN")}</span>
          </div>
          <button data-testid="intra-start-btn" onClick={start} disabled={working}
            className="w-full md:w-auto px-6 py-3 bg-[#FFCC00] hover:bg-[#FFD700] text-black font-semibold rounded-sm flex items-center justify-center gap-2 disabled:opacity-50">
            {working ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
            Start NAVA-INTRA
          </button>
        </div>
      ) : (
        <div className="border border-zinc-900 bg-[#0F0F0F] rounded-md p-4 md:p-5 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] grid grid-cols-3 gap-3">
            <Stat label="Capital" value={`₹${Number(bot.capital).toLocaleString("en-IN")}`} />
            <Stat label="Leverage" value={`${bot.leverage}x`} accent="#FFCC00" />
            <Stat label="Notional" value={`₹${(bot.capital * bot.leverage).toLocaleString("en-IN")}`} accent="#00E676" />
          </div>
          <button data-testid="intra-stop-btn" onClick={stop} disabled={working}
            className="px-4 py-2.5 bg-[#FF3B30]/10 hover:bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/30 rounded-sm flex items-center gap-2 text-sm">
            <Power className="w-4 h-4" /> Stop
          </button>
          <button data-testid="intra-refresh-btn" onClick={reload} className="px-3 py-2.5 bg-zinc-900 border border-zinc-800 hover:border-zinc-600 text-zinc-300 text-sm rounded-sm flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            data-testid="intra-pdf-btn"
            onClick={downloadPdf}
            className="px-3 py-2.5 bg-[#FFCC00]/10 hover:bg-[#FFCC00]/20 border border-[#FFCC00]/30 text-[#FFCC00] text-sm rounded-sm flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> <span className="hidden sm:inline">PDF</span>
          </button>
          <div
            data-testid="intra-learning-badge"
            title="Adaptive-learning lessons accumulated from past trades — the more, the smarter"
            className="px-3 py-2.5 bg-[#A78BFA]/10 border border-[#A78BFA]/30 text-[#A78BFA] text-xs rounded-sm flex items-center gap-2 font-mono"
          >
            <Brain className="w-4 h-4" />
            <span>{lessons.count} lessons</span>
          </div>
        </div>
      )}

      {/* Session clock */}
      {active && (
        <div className="border border-zinc-900 bg-[#0F0F0F] rounded-md p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 text-[#FFCC00]" />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">IST Time</div>
              <div className="font-mono text-sm text-white">{istTime || "—"}</div>
            </div>
          </div>
          <div className="text-xs text-zinc-500 font-mono">
            {marketOpen ? "Scanning every 3 min · auto square-off at 15:15 IST" : "Engine paused outside NSE hours (09:15 – 15:30 IST)"}
          </div>
        </div>
      )}

      {/* Active intraday signals */}
      {active && (
        <Panel
          title="Active Intraday Positions"
          subtitle={`${activeSignals.length} of 2 max`}
          right={activeSignals.length > 0 && tape?.positions?.intra?.length > 0 ? (
            <span data-testid="intra-live-pnl" className={`px-2.5 py-1 rounded-sm text-[11px] font-mono uppercase tracking-wider ${totalUnrealized >= 0 ? "text-[#00E676] bg-[#00E676]/10 border border-[#00E676]/30" : "text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/30"}`}>
              Live P&amp;L {totalUnrealized >= 0 ? "+" : ""}₹{totalUnrealized.toFixed(2)}
            </span>
          ) : null}
        >
          {activeSignals.length === 0 ? (
            <EmptyHint>
              {marketOpen ? "Scanning NSE for high-conviction intraday setups (≥75%). Next scan within 3 min." : "Market closed. Engine will resume at 9:15 AM IST."}
            </EmptyHint>
          ) : (
            <div className="space-y-3">
              <AnimatePresence>
                {activeSignals.map(s => <IntraSignalCard key={s.id} s={s} mark={intraMarks[s.id]} onClose={() => closeSignal(s.id)} />)}
              </AnimatePresence>
            </div>
          )}
        </Panel>
      )}

      {/* Watchlist */}
      {active && watch.length > 0 && (
        <Panel title="Near-Trigger Watch" subtitle="Setups awaiting confirmation">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {watch.slice(0, 6).map((w, i) => (
              <div key={i} className="p-3 bg-black/30 border border-zinc-900 rounded-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-sm font-semibold">{w.ticker}</span>
                  {w.level && <span className="font-mono text-xs text-[#FFCC00]">@ ₹{w.level}</span>}
                </div>
                <div className="text-xs text-zinc-400">{w.trigger}</div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Closed signals */}
      {closedSignals.length > 0 && (
        <Panel
          title="Today's Closed Positions"
          subtitle="Auto-closed on stop / TP2 or 15:15 IST square-off"
          right={
            <span className={`px-2.5 py-1 rounded-sm text-[11px] font-mono uppercase tracking-wider ${totalRealized >= 0 ? "text-[#00E676] bg-[#00E676]/10 border border-[#00E676]/30" : "text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/30"}`}>
              P&amp;L {totalRealized >= 0 ? "+" : ""}₹{totalRealized.toFixed(2)}
            </span>
          }
        >
          <div className="space-y-2">{closedSignals.map(s => <IntraClosedRow key={s.id} s={s} />)}</div>
        </Panel>
      )}

      {/* Status narrative */}
      {portfolio && (
        <Panel title="Portfolio Status" subtitle={`Updated ${portfolio.ist_time || ""}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Stat label="Phase" value={portfolio.session_phase || "—"} accent="#FFCC00" />
            <Stat label="Active" value={portfolio.active_count ?? 0} />
            <Stat label="Notional" value={`₹${Number(portfolio.deployed_notional_inr || 0).toLocaleString("en-IN")}`} />
            <Stat label="Risk" value={portfolio.risk_level || "Normal"} accent={portfolio.risk_level === "Elevated" ? "#FF3B30" : "#00E676"} />
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed">{portfolio.narrative}</p>
          {portfolio.next_action_hint && <div className="mt-2 px-3 py-2 border-l-2 border-[#FFCC00] bg-[#FFCC00]/5 text-xs text-zinc-300"><span className="text-[#FFCC00] font-mono">Next:</span> {portfolio.next_action_hint}</div>}
        </Panel>
      )}

      {/* 9-Stage Adaptive Learning */}
      {active && <LearningPanel fetcher={intraLearning} accent="#FFCC00" />}
    </div>
  );
}

// ---------- Sub-components ----------
function Pill({ label, color, icon }) {
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border" style={{ borderColor: `${color}40`, background: `${color}10`, color }}>
      {icon || <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: color }} />}
      <span className="font-mono text-[10px] uppercase tracking-wider">{label}</span>
    </div>
  );
}
function Field({ label, sub, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-zinc-500 font-mono mb-1.5">{label}</label>
      {children}
      {sub && <p className="text-[10px] text-zinc-600 mt-1">{sub}</p>}
    </div>
  );
}
function Stat({ label, value, accent = "#FFF" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{label}</div>
      <div className="font-mono text-sm font-bold mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}
function Panel({ title, subtitle, right, children }) {
  return (
    <div className="border border-zinc-900 bg-[#0F0F0F] rounded-md p-4 md:p-5">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-base md:text-lg font-semibold">{title}</h2>
          {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}
function EmptyHint({ children }) {
  return (
    <div className="flex items-center gap-3 p-4 bg-black/30 border border-zinc-900 rounded-sm">
      <Activity className="w-5 h-5 text-[#FFCC00] animate-pulse flex-shrink-0" />
      <p className="text-sm text-zinc-400">{children}</p>
    </div>
  );
}

function IntraSignalCard({ s, mark, onClose }) {
  const isLong = s.direction === "LONG";
  const dirColor = isLong ? "#00E676" : "#FF3B30";
  const up = mark && Number(mark.unrealized_pnl) >= 0;
  return (
    <motion.div
      data-testid={`intra-signal-${s.id}`}
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
      className="border border-zinc-800 bg-black/40 rounded-md overflow-hidden"
    >
      <div className="flex items-center justify-between p-3 border-b border-zinc-900 bg-zinc-900/30">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          {isLong ? <ArrowUpRight className="w-4 h-4" style={{ color: dirColor }} /> : <ArrowDownRight className="w-4 h-4" style={{ color: dirColor }} />}
          <span className="font-mono text-sm font-semibold">{s.ticker?.replace(".NS", "")}</span>
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm" style={{ color: dirColor, background: `${dirColor}15`, border: `1px solid ${dirColor}40` }}>{s.direction}</span>
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm text-[#FFCC00] bg-[#FFCC00]/10 border border-[#FFCC00]/30">{s.leverage}x</span>
          <span className="text-[10px] font-mono text-zinc-500">conv {s.conviction}%</span>
        </div>
        <button onClick={onClose} data-testid={`intra-close-${s.id}`} className="text-zinc-500 hover:text-[#FF3B30]"><X className="w-4 h-4" /></button>
      </div>
      {mark && (
        <div data-testid={`intra-live-${s.id}`} className="p-3 grid grid-cols-3 gap-3 border-b border-zinc-900" style={{ background: up ? "rgba(0,230,118,0.06)" : "rgba(255,59,48,0.06)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] pulse-dot" /> LTP Live
            </div>
            <div className="font-mono text-sm font-bold mt-0.5 text-white">₹{Number(mark.price).toLocaleString("en-IN")}</div>
          </div>
          <Stat label="Unrealized P&L" value={`${up ? "+" : ""}₹${Number(mark.unrealized_pnl).toLocaleString("en-IN")}`} accent={up ? "#00E676" : "#FF3B30"} />
          <Stat label="Move" value={`${Number(mark.pnl_pct) >= 0 ? "+" : ""}${mark.pnl_pct}%`} accent={Number(mark.pnl_pct) >= 0 ? "#00E676" : "#FF3B30"} />
        </div>
      )}
      <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-3 border-b border-zinc-900">
        <Stat label="Entry" value={s.entry != null ? `₹${s.entry}` : "—"} />
        <Stat label="Stop" value={s.stop != null ? `₹${s.stop}` : "—"} accent="#FF3B30" />
        <Stat label="TP1" value={s.tp1 != null ? `₹${s.tp1}` : "—"} accent="#00E676" />
        <Stat label="TP2" value={s.tp2 != null ? `₹${s.tp2}` : "—"} accent="#00E676" />
      </div>
      <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-3 border-b border-zinc-900">
        <Stat label="Qty" value={s.qty || "—"} />
        <Stat label="Notional" value={s.notional_inr ? `₹${Number(s.notional_inr).toLocaleString("en-IN")}` : "—"} />
        <Stat label="Max Loss" value={s.max_loss_inr ? `-₹${Number(s.max_loss_inr).toLocaleString("en-IN")}` : "—"} accent="#FF3B30" />
        <Stat label="Hold" value={s.hold_minutes ? `${s.hold_minutes} min` : "—"} />
      </div>
      <div className="p-3 space-y-1.5 text-xs">
        {s.rationale_setup && <Row icon="setup" text={s.rationale_setup} />}
        {s.rationale_momentum && <Row icon="mom" text={s.rationale_momentum} />}
        {s.rationale_sector && <Row icon="sec" text={s.rationale_sector} />}
        {s.exit_plan && <Row icon="exit" text={s.exit_plan} />}
        {s.invalidation && <Row icon="inv" text={s.invalidation} />}
      </div>
    </motion.div>
  );
}

function Row({ icon, text }) {
  const labels = { setup: "Setup", mom: "Momentum", sec: "Sector", exit: "Exit", inv: "Invalidation" };
  const colors = { setup: "#FFCC00", mom: "#00E676", sec: "#007AFF", exit: "#A78BFA", inv: "#FF3B30" };
  return (
    <div className="flex gap-2">
      <span className="font-mono text-[10px] uppercase tracking-wider flex-shrink-0 mt-0.5 w-20" style={{ color: colors[icon] }}>{labels[icon]}</span>
      <p className="text-zinc-300 leading-relaxed">{text}</p>
    </div>
  );
}

function IntraClosedRow({ s }) {
  const isOption = (s.instrument_type || "").toUpperCase() === "OPTION";
  const out = s.outcome || "";
  const isWin = out === "TP2" || out === "TP1" || (Number(s.realized_pnl) || 0) > 0;
  const color = isWin ? "#00E676" : out === "STOPPED" ? "#FF3B30" : "#A1A1AA";
  const Icon = isWin ? CheckCircle2 : out === "STOPPED" ? ShieldX : Target;
  const pnl = Number(s.realized_pnl) || 0;
  const closedAt = s.closed_at ? new Date(s.closed_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "";
  const label = isOption ? `${s.ticker} ${s.strike} ${s.option_type || s.direction}` : s.ticker?.replace(".NS", "");
  return (
    <div data-testid={`intra-closed-${s.id}`} className="flex flex-wrap items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-black/30 border border-zinc-900 rounded-sm">
      <Icon className="w-4 h-4 flex-shrink-0" style={{ color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm font-semibold">{label}</span>
          <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm" style={{ color, background: `${color}10`, border: `1px solid ${color}33` }}>{out.replace("_", " ")}</span>
          {isOption && <span className="text-[10px] font-mono text-[#A78BFA] px-1.5 py-0.5 rounded-sm bg-[#A78BFA]/10 border border-[#A78BFA]/30">OPT</span>}
          {!isOption && s.leverage && <span className="text-[10px] font-mono text-[#FFCC00] px-1.5 py-0.5 rounded-sm bg-[#FFCC00]/10 border border-[#FFCC00]/30">{s.leverage}x</span>}
        </div>
        <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
          {s.direction || s.option_type} · qty {s.qty} · close {isOption ? `₹${s.close_price ?? "—"} (spot ₹${s.close_spot ?? "—"})` : `₹${s.close_price ?? "—"}`} · {closedAt} IST
        </div>
      </div>
      <div className="font-mono text-sm font-semibold text-right ml-auto" style={{ color }}>
        {pnl >= 0 ? "+" : ""}₹{pnl.toFixed(2)}
      </div>
    </div>
  );
}
